import {
  useGetStaffListQuery,
} from "@/services/StaffsApi";
import { LoadingButton } from "@/components/ui/loading-button/LoadingButton";
import { Separator } from "@/components/ui/Separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import {DeleteCognitoFile, UploadCognitoFiles} from "@/utils/lib/S3Service";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { toast } from "@/components/ui/UseToast";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FloatingLabelInput } from "@/components/ui/floating-lable/Floating";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/ui/Button";
import { ChevronDownIcon, Eye, Trash, UploadCloud, User } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command";
import { Check } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { ConfirmationDialog } from "@/components/ui/common/ConfirmationDailog";
import { deepCompareObjects } from "@/utils/Helper";
import { Task, TaskCreationSchema, TaskPriority, TaskStatus } from "@/app/types/tasks";
import { useCreateTaskMutation, useUpdateTaskMutation } from "@/services/TasksApi";
import { Input } from "@/components/ui/Input";
import { InputTags } from "@/components/ui/input-tags/Tags";
import { TicketStatus, useGetAllListingOfTicketsQuery } from "@/services/LiveQueueService";
import { debounce } from "lodash";
import { AutoComplete } from "@/components/ui/auto-complete/AutoComplete";
import { Search } from "lucide-react";
import { Ticket as TicketInterface } from "@/services/LiveQueueService";
const status = [
  { value: "todo", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "hold", label: "Hold" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Archived" },
];

const priority = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" }
];


interface TaskFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: "add" | "edit";
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
  refetch: () => void;
  setData: React.Dispatch<React.SetStateAction<Task | undefined>>;
  data: Task | undefined;
  isgettingCreate?: boolean;
  initialTicket?:{
    ticket_id: string;
    ticket_number: string;
  }
}

interface Ticket {
  id: string;
  ticket_id: string;
}


const initialTask = {
  title: "",
  description: "",
  ticket_id: undefined as string | undefined,
  status: TaskStatus.TODO,
  due_date: "", // Format date as YYYY-MM-DD
  priority: TaskPriority.LOW,
  assigned_to: "",
  created_at: new Date().toISOString(),
  attachments: [],
  tags: [],
};

// Add these constants at the top of your file, with other constants
const ALLOWED_FILE_TYPES = [
  // Documents
  'application/pdf', // PDF
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
  'text/plain', // TXT
  
  // Images
  'image/png', // PNG
  'image/jpeg', // JPG, JPEG
];

const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.docx', '.xlsx', '.txt', '.png', '.jpg', '.jpeg'];
interface SelectedTicket{
ticket_id:number;
ticket_number:string;
}
const TaskForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  refetch,
  data,
  setData,
  isgettingCreate,
  initialTicket
}: TaskFormProps) => {
  const today = new Date();
  const localDate = today.toLocaleDateString("en-CA"); //
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SelectedTicket>();
  const [isDragging, setIsDragging] = useState(false);
  const [createTask] = useCreateTaskMutation();
  const [updateTask] = useUpdateTaskMutation();
  const [isFocused, setIsFocused] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const { data: staffOptions, refetch: staffAddRefetch } = useGetStaffListQuery(
    { query: "" }
  );

  const form = useForm<TaskCreationSchema>({
    mode: "all",
    defaultValues: initialTask,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

const debouncedSetSearchQuery = useMemo(
  () =>
    debounce((query: string) => {
      setDebouncedSearchQuery(query);
    }, 500),
  []
);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    debouncedSetSearchQuery(value);

    // Add this to ensure immediate update:
    if (!value.trim()) {
      setSearchResults(ticketOptions);
    } else {
      const filtered = ticketOptions.filter(
        (ticket) =>
          ticket.ticket_id?.toLowerCase().includes(value.toLowerCase()) ||
          ticket.title?.toLowerCase().includes(value.toLowerCase()) ||
          ticket.customer?.username?.toLowerCase().includes(value.toLowerCase())
      );
      setSearchResults(filtered);
    }
  };

  // Add this constant
  const MAX_FILE_SIZE_MB = 10; // 10MB max file size
  // Add this function to your component
  const validateFileType = (file: File): boolean => {
    // Check MIME type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      // If MIME type check fails, check extension as fallback
      const extension = "." + file.name.split(".").pop()?.toLowerCase();
      return ALLOWED_FILE_EXTENSIONS.some((ext) => extension === ext);
    }
    return true;
  };
  // Update the validation function
  const validateFile = (file: File): { valid: boolean; message?: string } => {
    // Check file type
    if (!validateFileType(file)) {
      return {
        valid: false,
        message:
          "Invalid file type. Allowed formats: PDF, DOCX, XLSX, TXT, PNG, JPG, JPEG.",
      };
    }

    // Check file size (convert MB to bytes)
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return {
        valid: false,
        message: `File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`,
      };
    }

    return { valid: true };
  };

  const queryString = useMemo(() => {
    const statuses = [
      TicketStatus.NO_SHOW,
      TicketStatus.IN_PROGRESS,
      TicketStatus.AWAITING_INFO,
      TicketStatus.HOLD,
      TicketStatus.CANCELLED,
      TicketStatus.COMPLETED,
    ];
    // Dynamically append statuses
    const statusQuery = statuses.map((status) => `status=${status}`).join("&");
    const searchParam = debouncedSearchQuery
      ? `&search=${debouncedSearchQuery}`
      : "";

    return `${statusQuery}${searchParam}&limit=100&offset=0`;
  }, [debouncedSearchQuery]);

  const {
    data: tickets,
    isLoading,
    isUninitialized,
    refetch: listReftch,
  } = useGetAllListingOfTicketsQuery({
    query: queryString,
  });

  const ticketsData = useMemo(() => {
    return Array.isArray(tickets?.data) ? tickets?.data : [];
  }, [tickets, queryString]);

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = form;

  useEffect(() => {
    if (action === "edit") {
      const payload = { ...data };
      setUploadedFiles(payload.attachments ?? []);
      setSearchQuery(payload.linked_ticket?.ticket_number || "");

      // Format the due_date for the date input (YYYY-MM-DD)
      console.log("due date", payload.due_date);
      // Format the due_date for the date input (YYYY-MM-DD)
      if (payload.due_date) {
        console.log("Original due_date:", payload.due_date);
        const dateObj = new Date(payload.due_date);
        console.log("Date object:", dateObj);
        const formattedDate = dateObj.toISOString().split("T")[0]; // Format as YYYY-MM-DD
        console.log("Formatted date:", formattedDate);
        const dateParts = payload.due_date.split('T')[0];
        console.log("Parsed date parts:", dateParts);
        payload.due_date = dateParts; 
      }
      // Set ticket_id directly from data, don't depend on selectedTicket state
      if (data?.linked_ticket?.ticket_id) {
        setSearchQuery(data.linked_ticket.ticket_number || "");
        console.log("data linked ticket", data.linked_ticket.ticket_id);
        setValue("ticket_id", data.linked_ticket.ticket_id.toString());
        setSelectedTicket({
          ticket_id: data.linked_ticket.ticket_id,
          ticket_number: data.linked_ticket.ticket_number || "",
        });
      }

      console.log("Final payload before reset:", payload);

      reset(payload);

      setTimeout(() => {
        console.log("Form values after reset:", watch());
      }, 100);
    } else if (action === "add") {
      reset(
        { ...initialTask, due_date: "" },
        { keepIsSubmitted: false, keepSubmitCount: false }
      );
      setValue("ticket_id", undefined);
      setSelectedTicket({
        ticket_id: 0,
        ticket_number: "",
      });
      setUploadedFiles([]);
      setSearchQuery("");
    }
  }, [action, data, setValue, reset]);

  const tableData = React.useMemo(() => {
    return Array.isArray(tickets?.data) ? tickets?.data : [];
  }, [tickets?.data]);

  const watcher = watch();
  useEffect(() => {
    if (debouncedSearchQuery !== "") {
      if (listReftch) {
        listReftch();
      }
    }
  }, [debouncedSearchQuery, listReftch]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);

      // Validate each file
      for (const file of newFiles) {
        const validation = validateFile(file);
        if (!validation.valid) {
          toast({
            variant: "destructive",
            title: "File validation error",
            description: validation.message,
          });
          return;
        }
      }

      setFiles([...files, ...newFiles]);
    }
  };
  // Handle drag-over event
  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  // Handle drag leave event
  const handleDragLeave = () => {
    setIsDragging(false);
  };

  // Update handleDrop function with combined validation
  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);

    if (event.dataTransfer.files) {
      const newFiles = Array.from(event.dataTransfer.files);

      // Validate each file
      for (const file of newFiles) {
        const validation = validateFile(file);
        if (!validation.valid) {
          toast({
            variant: "destructive",
            title: "File validation error",
            description: validation.message,
          });
          return;
        }
      }

      setFiles([...files, ...newFiles]);
    }
  };

  // Remove file before upload
  const handleRemoveFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  // Remove uploaded file
  const handleRemoveUploadedFile = (url: string) => {
    setUploadedFiles(uploadedFiles.filter((fileUrl) => fileUrl !== url));
    DeleteCognitoFile(url);
  };

  // Upload files
  const handleUpload = async () => {
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    try {
      const uploadedUrls = await UploadCognitoFiles(files);
      // Check if attachments exists and is an array before spreading
      const newAttachments = uploadedUrls.attachments || [];
      setUploadedFiles([...uploadedFiles, ...newAttachments]);
      setFiles([]);
      toast({
        variant: "success",
        title: "Files uploaded successfully",
      });
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        variant: "destructive",
        title: "An error occurred during upload",
      });
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const onSubmit = async (payload: TaskCreationSchema) => {
    delete payload.comments;

    try {
      payload.due_date = new Date(payload.due_date).toISOString();
      payload.created_at = new Date().toISOString();

      if (files.length > 0) {
        const uploadedUrls = await UploadCognitoFiles(files);
        if (uploadedUrls.success) {
          if (action === "edit") {
            // ✅ Combine previously uploaded files with newly uploaded ones
            payload.attachments = [
              ...uploadedFiles,
              ...(uploadedUrls.attachments ?? []),
            ];
            setUploadedFiles(payload.attachments); // Update stored uploaded files
          } else {
            // ✅ In "add" mode, only use newly uploaded files
            payload.attachments = uploadedUrls.attachments;
          }
          setFiles([]); // ✅ Clear new files after upload
        } else {
          throw new Error(uploadedUrls.message);
        }
      } else {
        // ✅ If no new files are uploaded, keep existing ones (edit mode)
        payload.attachments = uploadedFiles;
      }
      if (action === "add") {
        await createTask(payload)
          .unwrap()
          .then(() => {
            toast({ variant: "success", title: "Task created successfully" });
            refetch();
            handleClose();
          })
          .catch((err) => {
            toast({
              variant: "destructive",
              title: `${err?.data?.detail || err?.data?.message}`,
            });
          });
      } else if (action === "edit" && isgettingCreate === false) {
        console.log("payload", payload);
        await updateTask({
          ...payload,
          id: payload.task_id?.toString() ?? payload.id,
          ticket_id:
            selectedTicket?.ticket_id && selectedTicket.ticket_id > 0
              ? selectedTicket.ticket_id.toString()
              : null,
        })
          .unwrap()
          .then(() => {
            toast({ variant: "success", title: "Task updated successfully" });
            refetch();
            handleClose();
          })
          .catch((err) => {
            toast({
              variant: "destructive",
              title: `${err?.data?.detail || err?.data?.message}`,
            });
          });
      } else if (action === "edit" && isgettingCreate === true) {
        console.log(
          "payload on serve ticket",
          payload,
          selectedTicket?.ticket_id
        );
        await createTask({
          ...payload,
          ticket_id: selectedTicket?.ticket_id
            ? selectedTicket.ticket_id.toString()
            : undefined,
        })
          .unwrap()
          .then(() => {
            toast({ variant: "success", title: "Task created successfully" });
            refetch();
            handleClose();
          })
          .catch((err) => {
            toast({
              variant: "destructive",
              title: `${err?.data?.detail || err?.data?.message}`,
            });
          });
      }
    } catch (error: unknown) {
      console.error("Error:", error);
      toast({
        variant: "destructive",
        title: "Error in form submission",
        description: "Something went wrong.",
      });
    }
  };
useEffect(() => {
  if (initialTicket?.ticket_id) {
    setValue("ticket_id", initialTicket.ticket_id.toString());
    setSelectedTicket({
      ticket_id: parseInt(initialTicket.ticket_id, 10), // Convert string to number
      ticket_number: initialTicket.ticket_number || "",
    });
    setSearchQuery(initialTicket.ticket_number || "");
  }
}, [initialTicket, setValue]);
  // const getFileName = (url: string) => {
  //   return url.split("/").pop(); // Extracts the last part of the URL (file name)
  // };

  const getFileName = (url: string) => {
    const lastPart = url.split("/").pop() || "";
    // Remove UUID prefix if present and convert to lowercase
    const fileName = lastPart.replace(/^[\w-]+-/, "").toLowerCase();
    return fileName;
  };

  const getFileType = (url: string) => {
    const extension = url.split(".").pop()?.toUpperCase(); // Extracts the file extension
    return extension ? extension : "Unknown";
  };

  const handleClose = () => {
    clearErrors();
    reset();
    setAction("add");
    setData(undefined);
    setOpen(false);
    setSearchQuery("");
    setFiles([]);
    setUploadedFiles([]);
    if (
      listReftch && // Ensure refetch function exists
      !isUninitialized && // Ensure the query is not uninitialized
      !isLoading // Ensure the query is not currently loading for the first time
    ) {
      listReftch();
    }
    setSearchQuery("");
    setSelectedTicket({
      ticket_id: 0,
      ticket_number: "",
    });
    if (listReftch && !isUninitialized && !isLoading) {
      listReftch();
    }
  };
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const openDialog = () => {
    const { flag, differences } = deepCompareObjects(watcher, initialTask);
    if (!flag) {
      setIsDialogOpen(true);
    } else {
      handleClose();
    }
  };
  const closeDialog = () => setIsDialogOpen(false);
  const handleConfirm = async () => {
    closeDialog();
    handleClose();
  };

  // const handleTicketSelect = (selectedUser: Ticket) => {
  //   setValue("ticket_id", selectedUser.id.toString());
  //   setSelectedTicket({
  //     ticket_id: parseInt(selectedUser.id),
  //     ticket_number: selectedUser.ticket_id,
  //   });
  // };

  const handleTicketSelect = (user: Ticket) => {
    // Parse the ID correctly
    const ticketId = parseInt(user.id.toString(), 10);

    // Update the form value
    setValue("ticket_id", ticketId.toString());

    // Set selectedTicket with NUMBER for ticket_id
    setSelectedTicket({
      ticket_id: ticketId,
      ticket_number: user.ticket_id || "",
    });

    setSearchQuery(user.ticket_id || "");
    
    // Don't clear search results immediately - let the blur handler do that
    // setTimeout is used to ensure the click is processed before closing dropdown
    setTimeout(() => setIsFocused(false), 100);
  };

  const ticketOptions = useMemo(() => {
    return (
      ticketsData
        ?.filter((data) => data.ticket_number?.startsWith("S-"))
        // .filter((data) => data.ticket_id && !data.ticket_id.includes("R"))
        .map((data) => ({
          ...data,
          value: data.id.toString(),
          label: data.ticket_id,
          title: data.title,
        })) || []
    );
  }, [ticketsData]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(ticketOptions);
    }
  }, [ticketOptions]);

  useEffect(() => {
    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery]);

  
  return (
    <Sheet open={isOpen}>
      <SheetContent
        hideCloseButton
        className="!max-w-[1050px] p-0 custom-scrollbar  sm:w-[90%] sm:max-w-2xl "
      >
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit, onError)}
            className="flex flex-col  gap-4 bg-gray-100 h-screen"
            noValidate
          >
            <SheetHeader className="sticky top-0 z-40 px-4 py-3 bg-white border-2 border-transparent border-b-gray-200">
              <SheetTitle>
                <div className="flex justify-between gap-5 items-center  bg-white">
                  <p className="font-semibold">
                    {isgettingCreate
                      ? "Create"
                      : action === "add"
                        ? "Create"
                        : "Edit"}{" "}
                    Task
                  </p>

                  <div className="flex gap-2">
                    <Button
                      type={"button"}
                      // onClick={handleClose}
                      onClick={openDialog}
                      className="w-[100px] gap-2  bg-transparent border border-primary text-black hover:border-primary hover:bg-muted"
                    >
                      <i className="fa-regular fa-x  text-xs font-semibold "></i>
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      {!isSubmitting && (
                        <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                      )}
                      Save
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <SheetDescription className="p-4 mx-4 bg-white rounded-lg">
              <h2 className="font-medium text-lg">Task Details</h2>
              <Separator className=" h-[1px] rounded-full my-2" />

              <div className="grid grid-cols-2 slg:grid-cols-3 gap-4 pt-2">
                <FloatingLabelInput
                  id="title"
                  label="Title"
                  className="capitalize w-full"
                  text="*"
                  {...register("title", {
                    required: "Required",
                    setValueAs: (value) => value.toLowerCase(),
                    maxLength: {
                      value: 100,
                      message: "Should be 100 characters or less",
                    },
                  })}
                  error={errors.title?.message}
                />

                <div className="relative w-full ">
                  <Controller
                    name="status"
                    rules={{ required: "Required" }}
                    control={control}
                    render={({
                      field: { onChange, value, onBlur },
                      fieldState: { invalid, error },
                    }) => {
                      const statusLabel = status.filter(
                        (r) => r.value == value
                      )[0];
                      return (
                        <Select
                          onValueChange={(value) => onChange(value)}
                          defaultValue={value}
                        >
                          <SelectTrigger
                            className=" w-full"
                            floatingLabel="Status"
                            text="*"
                          >
                            <SelectValue
                              placeholder="Select status"
                              className="text-gray-400"
                            >
                              <span className="flex gap-2 items-center">
                                <span>{statusLabel?.label}</span>
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {status.map((item) => (
                              <SelectItem key={item.value} value={item.value}>
                                {item.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    }}
                  />
                </div>

                <div className="relative w-full ">
                  <Controller
                    name="priority"
                    rules={{ required: "Required" }}
                    control={control}
                    render={({
                      field: { onChange, value, onBlur },
                      fieldState: { invalid, error },
                    }) => {
                      const statusLabel = priority.filter(
                        (r) => r.value == value
                      )[0];
                      return (
                        <Select
                          onValueChange={(value) => onChange(value)}
                          defaultValue={value}
                        >
                          <SelectTrigger
                            className="capitalize w-full"
                            floatingLabel="Priority"
                            text="*"
                          >
                            <SelectValue
                              placeholder="Select status"
                              className="text-gray-400"
                            >
                              <span className="flex gap-2 items-center">
                                <span>{statusLabel?.label}</span>
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {priority.map((item) => (
                              <SelectItem key={item.value} value={item.value}>
                                {item.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    }}
                  />
                </div>
                <div className="">
                  <Controller
                    name={"assigned_to"}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <>
                        <div className="relative w-full">
                          <label className="absolute text-xs font-medium left-2 -top-2 z-10 bg-white px-1 text-gray-600">
                            Assignee
                            {/* <span className="text-red-500">*</span> */}
                          </label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button className="capitalize w-full scale-100 shadow-sm border-checkboxborder border-[1px] bg-white text-gray-600 hover:bg-white">
                                <span className="w-full text-left font-normal">
                                  {value
                                    ? staffOptions?.find(
                                        (role) => role.value === value
                                      )?.label
                                    : "Select Assignee"}
                                </span>
                                <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="p-0 relative"
                              side="bottom"
                            >
                              <Command className="w-full">
                                <CommandList>
                                  <CommandInput placeholder="Select Role" />
                                  <CommandEmpty>No Staff found.</CommandEmpty>
                                  <CommandGroup>
                                    {staffOptions?.map((role: any) => (
                                      <CommandItem
                                        value={role.value}
                                        key={role.value}
                                        className="capitalize"
                                        onSelect={() =>
                                          setValue("assigned_to", role.value)
                                        }
                                      >
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                            role.value == value
                                              ? "opacity-100"
                                              : "opacity-0"
                                          )}
                                        />
                                        {role.label}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                          {errors.assigned_to?.message || ""}
                        </div>
                      </>
                    )}
                  />
                </div>
                <div className="pb-2">
                  <Controller
                    name="due_date"
                    control={control}
                    rules={{
                      required: "Required",
                      validate: (value) => {
                        if (action === "add") {
                          // Always validate for add mode
                          if (
                            value &&
                            new Date(value).getTime() <
                              new Date().setHours(0, 0, 0, 0)
                          ) {
                            return "You cannot select a past date!";
                          }
                        } else if (action === "edit") {
                          // For edit mode, only validate if the date has been changed from original
                          if (value !== data?.due_date) {
                            if (
                              value &&
                              new Date(value).getTime() <
                                new Date().setHours(0, 0, 0, 0)
                            ) {
                              return "You cannot select a past date!";
                            }
                          }
                        }
                        return true;
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <div className="relative w-full">
                        <label className="absolute text-xs font-medium left-2 -top-2 z-10 bg-white px-1 text-gray-600">
                          Due Date<span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="date"
                          value={field.value || ""}
                          onChange={(e) => {
                            const selectedDate = new Date(e.target.value);
                            if (action === "add") {
                              // Always validate for add mode
                              if (
                                selectedDate.getTime() <
                                new Date().setHours(0, 0, 0, 0)
                              ) {
                                toast({
                                  variant: "destructive",
                                  title: "You cannot select a past date!",
                                });
                                return;
                              }
                            } else if (action === "edit") {
                              // For edit mode, only validate if the date has been changed
                              if (e.target.value !== data?.due_date) {
                                if (
                                  selectedDate.getTime() <
                                  new Date().setHours(0, 0, 0, 0)
                                ) {
                                  toast({
                                    variant: "destructive",
                                    title: "You cannot select a past date!",
                                  });
                                  return;
                                }
                              }
                            }
                            field.onChange(e.target.value);
                          }}
                          min={action === "add" ? localDate : undefined}
                          className="border rounded p-2 w-full flex justify-center items-center"
                        />
                        {fieldState.error && (
                          <p className="text-red-500">
                            {fieldState.error.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
                <div>
                  <Controller
                    control={control}
                    name="ticket_id"
                    render={({ field: { onChange, value, ref } }) => (
                      <div className="pb-2">
                        <div className="flex items-center relative w-auto">
                          <i className="fa-solid fa-magnifying-glass size-4 text-gray-400 absolute left-1 z-10 ml-2"></i>
                          <FloatingLabelInput
                            placeholder={"Search"}
                            icon={<Search />}
                            value={searchQuery}
                            onFocus={() => setIsFocused(true)} // Show dropdown on focus
                            onBlur={(e) => {
                              // Delay hiding dropdown to allow click events to register
                              setTimeout(() => {
                                if (document.activeElement?.id !== 'specificInput') {
                                  setIsFocused(false);
                                }
                              }, 200);
                            }}
                            onChange={(e) => {
                              const val = e.target.value.toLowerCase();
                              setSearchQuery(e.target.value);
                              debouncedSetSearchQuery(e.target.value);

                              // Only filter if we have options and a search query
                              if (ticketOptions.length > 0 && val) {
                                const filtered = ticketOptions.filter(
                                  (ticket) => {
                                    return (
                                      (ticket.ticket_id && ticket.ticket_id.toLowerCase().includes(val)) ||
                                      (ticket.title && ticket.title.toLowerCase().includes(val)) ||
                                      (ticket.customer?.username && ticket.customer.username.toLowerCase().includes(val))
                                    );
                                  }
                                );
                                setSearchResults(filtered);
                                setIsFocused(true); // Always show dropdown when typing
                              } else if (!val) {
                                // If search is cleared, show all options
                                setSearchResults(ticketOptions);
                              }
                            }}
                            id="specificInput"
                            className="specific w-80 lg:w-80 pl-8 text-sm placeholder:text-sm text-gray-400"
                          />
                        </div>
                        
                        {/* Improved dropdown with better visibility control */}
                        {isFocused && searchResults.length > 0 && (
                          <div className="mb-4 border rounded p-2 max-h-40 overflow-y-auto fancy-scroll absolute bg-white z-10 max-w-[22.5rem] shadow-lg">
                            {searchResults.length === 0 ? (
                              <div className="p-2 text-gray-500">No results found</div>
                            ) : (
                              searchResults.map((user) => (
                                <div
                                  key={user.id}
                                  className="p-2 border-b last:border-none cursor-pointer hover:bg-gray-100 flex flex-col items-start"
                                  onClick={() => handleTicketSelect(user)}
                                >
                                  <div className="flex flex-col w-full">
                                    {user.customer?.username ? (
                                      <span className="font-medium">{user.customer.username}</span>
                                    ) : null}
                                    {user.title ? (
                                      <span className="text-sm text-gray-600">{user.title}</span>
                                    ) : null}
                                    <span className="text-xs text-blue-600 font-semibold">
                                      {user.ticket_id || "No ID"}
                                    </span>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="flex flex-row w-full gap-2">
                <div className="w-full">
                  <FloatingLabelInput
                    id="description"
                    placeholder="Enter Description"
                    type="textarea"
                    rows={6}
                    maxLength={250}
                    {...register("description", {
                      maxLength: {
                        value: 250,
                        message: "Should be 250 characters or less",
                      },
                    })}
                    error={errors.description?.message}
                  />
                </div>
              </div>
              <div className="flex flex-col pt-2 w-full gap-2">
                <span>Tags</span>
                <Controller
                  name="tags"
                  control={control}
                  defaultValue={[]}
                  rules={{
                    validate: {
                      maxTags: (value) =>
                        (value?.length ?? 0) <= 10 || "Maximum 10 tags allowed",
                      uniqueTags: (value) =>
                        new Set(value).size === value?.length ||
                        "Duplicate tags are not allowed",
                    },
                  }}
                  render={({ field }) => (
                    <InputTags
                      className="h-full"
                      value={field.value || []}
                      onChange={(newTags) => {
                        field.onChange(newTags);
                      }}
                    />
                  )}
                />
                {errors.tags?.message && (
                  <span className="text-destructive font-poppins block !mt-[5px] text-xs">
                    {errors.tags?.message}
                  </span>
                )}
              </div>
              <div className="w-full pt-2">
                <span>Attachments :</span>

                <div className="flex flex-col items-center space-y-4 p-4 border rounded-lg shadow-md bg-white">
                  <label
                    className={`cursor-pointer flex flex-col items-center gap-2 p-4 border-2 ${
                      isDragging
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-300 bg-gray-50"
                    } border-dashed rounded-lg hover:bg-gray-100 w-full`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <UploadCloud className="w-8 h-8 text-gray-600" />
                    <span className="text-sm text-gray-600">
                      Drag & Drop or Click to Upload
                    </span>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      accept=".pdf,.docx,.xlsx,.txt,.png,.jpg,.jpeg"
                      className="hidden"
                    />
                  </label>

                  {/* Selected Files (Before Upload) */}
                  {files.length > 0 && (
                    <div className="w-full">
                      <ul className="max-h-40 overflow-y-auto border rounded p-2 bg-gray-50">
                        {files.map((file, index) => (
                          <li
                            key={index}
                            className="flex justify-between items-center text-sm p-1 border-b"
                          >
                            <span>{file.name}</span>
                            <Trash
                              className="w-6 h-6 text-red-500 cursor-pointer"
                              onClick={() => handleRemoveFile(index)}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Uploaded Files (After Upload) */}
                  {uploadedFiles.length > 0 && (
                    <div className="w-full">
                      <h3 className="text-sm font-semibold mb-2">
                        Uploaded Files:
                      </h3>
                      <ul className="max-h-40 overflow-y-auto border rounded p-2 bg-gray-50">
                        {uploadedFiles.map((url, index) => (
                          <li
                            key={index}
                            className="flex justify-between items-center text-sm p-1 border-b"
                          >
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline flex items-center gap-1"
                            >
                              <Eye className="w-4 h-4" /> View File{" "}
                              {getFileName(url)} ({getFileType(url)})
                            </a>
                            <Trash
                              className="w-4 h-4 text-red-500 cursor-pointer"
                              onClick={() => handleRemoveUploadedFile(url)}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </SheetDescription>
            <ConfirmationDialog
              isOpen={isDialogOpen}
              onClose={closeDialog}
              onConfirm={handleConfirm}
            />
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default TaskForm;
